import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Strikethrough, Code, Heading2, Heading3, List, ListOrdered,
  Quote, Minus, Undo2, Redo2, Link2, Unlink, Eraser,
} from "lucide-react";

const ToolbarBtn = ({ onClick, active, disabled, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors ${
      active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : ""
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="mx-1 h-5 w-px bg-slate-200" />;

/**
 * Premium Tiptap-powered rich text editor.
 * Stores HTML in `value`; calls onChange(html) on every edit.
 */
export const RichTextField = ({ label, value, onChange, placeholder, minHeight = 320, testid = "rich-text" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-emerald-700 underline underline-offset-2 hover:text-emerald-800" } }),
      Placeholder.configure({ placeholder: placeholder || "Write content…" }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: "legal-prose max-w-none focus:outline-none px-4 py-3",
        "data-testid": `${testid}-editor`,
      },
    },
  });

  // Sync external value updates (e.g. when admin switches pages)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div data-testid={testid}>
      {label && <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{label}</div>}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500/40 focus-within:border-emerald-300 transition-all">
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50/60 sticky top-0 z-10" data-testid={`${testid}-toolbar`}>
          <ToolbarBtn title="Bold (⌘B)" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testid="rt-bold"><Bold className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} testid="rt-italic"><Italic className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} testid="rt-strike"><Strikethrough className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Inline code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()} testid="rt-code"><Code className="h-4 w-4" /></ToolbarBtn>
          <Divider />
          <ToolbarBtn title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testid="rt-h2"><Heading2 className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testid="rt-h3"><Heading3 className="h-4 w-4" /></ToolbarBtn>
          <Divider />
          <ToolbarBtn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testid="rt-ul"><List className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} testid="rt-ol"><ListOrdered className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} testid="rt-quote"><Quote className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()} testid="rt-hr"><Minus className="h-4 w-4" /></ToolbarBtn>
          <Divider />
          <ToolbarBtn title="Add / edit link" active={editor.isActive("link")} onClick={promptLink} testid="rt-link"><Link2 className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Remove link" disabled={!editor.isActive("link")} onClick={() => editor.chain().focus().unsetLink().run()} testid="rt-unlink"><Unlink className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} testid="rt-clear"><Eraser className="h-4 w-4" /></ToolbarBtn>
          <Divider />
          <ToolbarBtn title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} testid="rt-undo"><Undo2 className="h-4 w-4" /></ToolbarBtn>
          <ToolbarBtn title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} testid="rt-redo"><Redo2 className="h-4 w-4" /></ToolbarBtn>
          <div className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-slate-400 pr-1">
            HTML
          </div>
        </div>
        <div style={{ minHeight }}>
          <EditorContent editor={editor} />
        </div>
      </div>
      <div className="mt-1 text-[10.5px] text-slate-400">
        Tip: use H2 for top-level sections (they auto-populate the page&apos;s Table of Contents).
      </div>
    </div>
  );
};

export default RichTextField;
